import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { VideoFormatEnum } from '../../common/general-types/briefs.dto';
import { ImageTypesEnum, S3BucketEnum } from '../../common/general-types/media-assets.dto';
import { ImageService } from '../../services/image/image.service';
import { S3uploaderService } from '../../services/s3uploader/s3uploader.service';
import { VideoService } from '../../services/video/video.service';

// const PLACEHOLDER_IMAGE = '/assets/images/placeholder.jpeg';
const PLACEHOLDER_IMAGE = '/assets/images/placeholder-video.png';

@Component({
  selector: 'atom-video-thumbnail',
  standalone: true,
  imports: [],
  templateUrl: './video-thumbnail.component.html',
  styleUrl: './video-thumbnail.component.css',
})
export class VideoThumbnailComponent implements OnChanges {
  @Input() videoId?: string;
  @Input() videoUrl!: string;
  @Input() videoFormat!: VideoFormatEnum;
  @Input() thumbnailURL: string | undefined;
  @Input() cdnKey: string | undefined;
  @Input() seekTo: number = 1.0;

  @Input() hideError: boolean = false;

  @Input() thumb_cdn_size: string = 'original';

  @Output() click = new EventEmitter<MouseEvent>();

  @Output() thumbnailPersisted = new EventEmitter<{ videoId: string; thumbnailURL: string }>();

  readonly placeholderImage = PLACEHOLDER_IMAGE;

  thumbnail = signal<string | null>(null);
  loading = signal<boolean>(false);
  error = signal<boolean>(false);
  imgSourceIndex = signal(0);

  private persistedThumbnailUrl?: string;
  private persistInFlight = false;
  private allSourcesFailed = false;

  public imageService = inject(ImageService);
  private videoService = inject(VideoService);
  private s3UploaderService = inject(S3uploaderService);

  ngOnChanges(changes: SimpleChanges): void {
    this.imgSourceIndex.set(0);
    this.error.set(false);
    this.allSourcesFailed = false;

    if (this.hasServerThumbnailSource()) {
      this.thumbnail.set(null);
      this.loading.set(false);
      return;
    }

    const shouldGenerate =
      (!!changes['videoUrl'] && this.videoUrl) ||
      (!!changes['thumbnailURL'] && !this.hasUsableThumbnailUrl() && this.videoUrl) ||
      (!!changes['cdnKey'] && !this.cdnKey && this.videoUrl);

    if (shouldGenerate && this.videoUrl) {
      if (this.isTikwmUrl(this.videoUrl)) {
        this.thumbnail.set(null);
        this.error.set(true);
        return;
      }
      this.createThumbnail(this.videoUrl, this.seekTo);
      return;
    }

    if (!this.videoUrl && !this.hasServerThumbnailSource() && !this.thumbnail()) {
      this.error.set(true);
    }
  }

  hasServerThumbnailSource(): boolean {
    if (this.allSourcesFailed) {
      return !!this.persistedThumbnailUrl;
    }
    return (
      this.hasUsableThumbnailUrl() ||
      !!this.cdnKey ||
      !!this.persistedThumbnailUrl
    );
  }

  showThumbnailBlock(): boolean {
    if (this.thumbnail() || this.persistedThumbnailUrl) {
      return true;
    }
    if (this.allSourcesFailed) {
      return false;
    }
    return this.hasUsableThumbnailUrl() || !!this.cdnKey;
  }

  showLoadingSkeleton(): boolean {
    return this.loading() && !this.hasServerThumbnailSource();
  }

  showErrorState(): boolean {
    return this.error() && !this.hideError && !this.hasServerThumbnailSource();
  }

  getDisplaySrc(): string {
    const sources = this.getOrderedSources();
    if (sources.length === 0) {
      return PLACEHOLDER_IMAGE;
    }
    const index = Math.min(this.imgSourceIndex(), sources.length - 1);
    return sources[index];
  }

  onImgError(): void {
    const sources = this.getOrderedSources();
    if (this.imgSourceIndex() < sources.length - 1) {
      this.imgSourceIndex.update((index) => index + 1);
      return;
    }

    this.allSourcesFailed = true;
    if (this.videoUrl && !this.isTikwmUrl(this.videoUrl) && !this.thumbnail()) {
      this.createThumbnail(this.videoUrl, this.seekTo);
      return;
    }
    this.error.set(true);
  }

  private getOrderedSources(): string[] {
    const sources: string[] = [];

    if (this.thumbnailURL && !this.isTikwmUrl(this.thumbnailURL)) {
      sources.push(this.thumbnailURL);
    }
    if (this.persistedThumbnailUrl) {
      sources.push(this.persistedThumbnailUrl);
    }
    if (this.cdnKey) {
      sources.push(this.imageService.getImageSource(this.cdnKey, this.thumb_cdn_size));
    }
    const generated = this.thumbnail();
    if (generated && !this.isTikwmUrl(generated)) {
      sources.push(generated);
    }

    return sources;
  }

  private hasUsableThumbnailUrl(): boolean {
    return !!(this.thumbnailURL && !this.isTikwmUrl(this.thumbnailURL));
  }

  private isTikwmUrl(url: string): boolean {
    return url.includes('www.tikwm.com');
  }

  async createThumbnail(fileOrUrl: string | File, seekTo: number = 1.0) {
    this.loading.set(true);
    this.error.set(false);
    try {
      const generatedThumbnail = await this.generateVideoThumbnail(
        fileOrUrl,
        seekTo,
        this.videoFormat,
      );
      this.thumbnail.set(generatedThumbnail);

      if (generatedThumbnail && this.videoId) {
        await this.persistThumbnail(generatedThumbnail);
      }

      this.loading.set(false);
    } catch {
      this.thumbnail.set(null);
      this.loading.set(false);
      if (!this.hasServerThumbnailSource()) {
        this.error.set(true);
      }
    }
  }

  private async persistThumbnail(generatedThumbnail: string): Promise<void> {
    if (this.persistInFlight || this.hasServerThumbnailSource()) {
      return;
    }

    this.persistInFlight = true;
    try {
      const fileFromThumbnail = this.dataUrlToFile(generatedThumbnail, 'video-thumbnail.png');
      if (!fileFromThumbnail) {
        console.warn('Failed to convert thumbnail data URL to File.');
        return;
      }

      const fileListLike = {
        length: 1,
        item: (index: number) => (index === 0 ? fileFromThumbnail : null),
        [Symbol.iterator]: function* () {
          yield fileFromThumbnail;
        },
      } as FileList;

      const uploadedThumbnail = await this.s3UploaderService.uploadFile(
        fileListLike,
        S3BucketEnum.COVER_PHOTOS,
        ImageTypesEnum.GALLERY_16_9,
        { silent: true },
      );

      if (!uploadedThumbnail) {
        console.warn('Thumbnail upload failed or was canceled.');
        return;
      }

      await firstValueFrom(
        this.videoService.sendVideoThumbnail(this.videoId!, uploadedThumbnail.fullUrl),
      );

      this.persistedThumbnailUrl = uploadedThumbnail.fullUrl;
      this.thumbnailPersisted.emit({
        videoId: this.videoId!,
        thumbnailURL: uploadedThumbnail.fullUrl,
      });
    } finally {
      this.persistInFlight = false;
    }
  }

  private dataUrlToFile(dataUrl: string, fileName: string): File | null {
    try {
      const [meta, data] = dataUrl.split(',');
      const mimeMatch = meta.match(/data:(.*);base64/);
      const mime = mimeMatch ? mimeMatch[1] : 'image/png';
      const binary = atob(data);
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mime });
      return new File([blob], fileName, { type: mime });
    } catch (err) {
      console.error('Error converting data URL to file', err);
      return null;
    }
  }

  /**
   * Generates a PNG data URL.
   * If videoFormat === Horizontal (1), crop to 16:9 (landscape) at 640x360.
   * Otherwise, keep the original frame dimensions.
   */
  generateVideoThumbnail(
    fileOrUrl: File | string,
    seekTo = 1.0,
    format?: VideoFormatEnum,
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';

      video.src = typeof fileOrUrl === 'string' ? fileOrUrl : URL.createObjectURL(fileOrUrl);

      const onError = () => reject('Failed to load video');

      video.onloadedmetadata = () => {
        const targetTime = Math.min(
          Math.max(seekTo, 0),
          isFinite(video.duration) ? video.duration - 0.01 : seekTo,
        );
        const onSeeked = () => {
          try {
            const isHorizontal = format === VideoFormatEnum.Horizontal;
            const ctxCanvas = document.createElement('canvas');

            if (isHorizontal) {
              const OUT_W = 640;
              const OUT_H = 360;
              ctxCanvas.width = OUT_W;
              ctxCanvas.height = OUT_H;

              const srcW = video.videoWidth;
              const srcH = video.videoHeight;
              const targetAspect = OUT_W / OUT_H;
              const srcAspect = srcW / srcH;

              let sx = 0;
              let sy = 0;
              let sw = srcW;
              let sh = srcH;

              if (srcAspect > targetAspect) {
                const newW = Math.round(srcH * targetAspect);
                sx = Math.floor((srcW - newW) / 2);
                sw = newW;
              } else if (srcAspect < targetAspect) {
                const newH = Math.round(srcW / targetAspect);
                sy = Math.floor((srcH - newH) / 2);
                sh = newH;
              }

              const ctx = ctxCanvas.getContext('2d');
              ctx?.drawImage(video, sx, sy, sw, sh, 0, 0, OUT_W, OUT_H);
            } else {
              ctxCanvas.width = video.videoWidth;
              ctxCanvas.height = video.videoHeight;
              const ctx = ctxCanvas.getContext('2d');
              ctx?.drawImage(video, 0, 0, ctxCanvas.width, ctxCanvas.height);
            }

            resolve(ctxCanvas.toDataURL('image/png'));
          } catch {
            reject('Failed to render frame');
          } finally {
            if (typeof fileOrUrl !== 'string') {
              URL.revokeObjectURL(video.src);
            }
          }
        };

        video.addEventListener('seeked', onSeeked, { once: true });
        const seek = () => {
          try {
            video.currentTime = targetTime;
          } catch {
            video.playbackRate = 1.0;
            video.currentTime = targetTime;
          }
        };
        if (video.readyState >= 1) {
          seek();
        } else {
          setTimeout(seek, 0);
        }
      };

      video.onerror = onError;
    });
  }
}
